/**
 * @file     ReadingLesson.tsx
 * @readme   ../README.md
 * @purpose  Reading lesson type: body text, key takeaways and the quiz, all server-rendered and visible without JavaScript.
 */
'use client';

import '@/core/i18n/deferred/academy';
import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { headingFont, hubColors, microLabelSx } from '@/components/hub/hubKitStyles';
import type { LessonContent } from '@/content-system/academy/types';
import { QuizSection } from '../quiz/QuizSection';
import { LegalDisclaimer } from '../LegalDisclaimer';
import { MortgageWidget } from '../tools/MortgageWidget';
import { DragDropQuiz } from '../quiz/DragDropQuiz';
import { LessonMarkdown } from '../LessonMarkdown';

const calloutSx = {
  px: { xs: 2.5, md: 3 },
  py: 2.5,
  borderRadius: `${layoutTokens.radius.lg}px`,
  backgroundColor: hubColors.surfaceSunken,
} as const;

interface ReadingLessonProps {
  content: LessonContent;
  onContentConsumed?: () => void;
  onQuizPassed?: () => void;
}

/** Fires onVisible once the ref is scrolled into view. Used to detect the user reached the end of a lesson. */
function ScrollSentinel({ onVisible }: { onVisible?: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || !onVisible) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible();
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [onVisible]);
  return <div ref={ref} aria-hidden style={{ height: 1 }} />;
}

export function ReadingLesson({ content, onContentConsumed, onQuizPassed }: ReadingLessonProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {content.readingBody && <LessonMarkdown>{content.readingBody}</LessonMarkdown>}

      {/* Slides as reading sections (fallback if slides provided) */}
      {content.slides?.map((slide, i) => (
        <Box key={i}>
          <Box
            component="h2"
            sx={{ m: 0, mb: 2, fontFamily: headingFont, fontSize: { xs: 22, md: 26 }, fontWeight: fontWeightTokens.semibold, letterSpacing: '-0.012em', lineHeight: 1.25, color: hubColors.textPrimary }}
          >
            {slide.title}
          </Box>
          <LessonMarkdown>{slide.body}</LessonMarkdown>
          {slide.tip && (
            <Box component="aside" sx={{ ...calloutSx, mt: 3 }}>
              <Box component="p" sx={{ ...microLabelSx, mb: 0.5 }}>
                {t('academy.lessonPage.tip', { defaultValue: 'Worth knowing' })}
              </Box>
              <Box component="p" sx={{ m: 0, fontSize: 16, lineHeight: 1.6, color: hubColors.textPrimary }}>
                {slide.tip}
              </Box>
            </Box>
          )}
        </Box>
      ))}

      {/* Drag-and-drop exercises */}
      {content.dragDrop && content.dragDrop.length > 0 && content.dragDrop.map((dd, idx) => (
        <DragDropQuiz
          key={idx}
          prompt={dd.prompt}
          items={dd.items}
          correctOrder={dd.correctOrder}
          explanation={dd.explanation}
        />
      ))}

      {/* Mortgage calculator widget for mortgage-related lessons */}
      {(content.moduleSlug === 'mortgage-preapproval' || content.moduleSlug === 'closing-in-bc' || content.moduleSlug === 'ptt-rebates') && (
        <MortgageWidget compact />
      )}

      {content.keyTakeaways.length > 0 && (
        <Box component="aside" sx={calloutSx}>
          <Box component="h2" sx={{ m: 0, mb: 1, fontSize: 16, fontWeight: fontWeightTokens.semibold, color: hubColors.textPrimary }}>
            {t('academy.lessonPage.takeaways', { defaultValue: 'Key takeaways' })}
          </Box>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {content.keyTakeaways.map((item, i) => (
              <Box component="li" key={i} sx={{ fontSize: 16, lineHeight: 1.6, color: hubColors.textPrimary, mb: 0.75, '&:last-child': { mb: 0 } }}>
                {item}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Completion sentinel, fires when user scrolls past the content */}
      <ScrollSentinel onVisible={onContentConsumed} />

      {content.quiz.length > 0 && <QuizSection questions={content.quiz} lessonId={content.id} onQuizPassed={onQuizPassed} />}

      <LegalDisclaimer sources={content.sources} lastVerified={content.lastVerified} />
    </Box>
  );
}
